import React from 'react';

import { LoaderTraceLogo, Pushbutton, RadioButton } from '@stratumn/atomic';

import { graphql } from '@apollo/client/react/hoc';
import { Calendar } from '@stratumn/icons';
import compose from 'lodash.flowright';
import moment from 'moment';
import PropType from 'prop-types';
import injectSheet from 'react-jss';

import { Footer, Navbar } from '@/components/layouts';
import { notify } from '@/components/toast';
import DatePicker from '@/components/ui/datepicker';
import { traceClient } from '@/gql';
import { FILTERS_QUERY } from '@/gql/localQueries';
import downloadData from './csv/default';
import queries from './queries';
import styles from './workflowExport.style';

const LIMIT = 5000;

export const WorkflowExport = ({ classes, match, workflowQuery }) => {
  const today = moment().toDate();
  const [isExporting, setIsExporting] = React.useState(false);
  const [startDate, setStartDate] = React.useState(null);
  const [endDate, setEndDate] = React.useState(null);
  const [fileType, setFileType] = React.useState('csv');
  const { loading, workflow } = workflowQuery;

  React.useEffect(() => {
    if (workflow && workflow.name)
      document.title = `Export data - ${workflow.name} - Trace`;
  }, [workflow]);

  const onStartDateChange = React.useCallback(date => setStartDate(date), []);
  const onEndDateChange = React.useCallback(date => setEndDate(date), []);
  const onSubmit = React.useCallback(async () => {
    setIsExporting(true);

    try {
      let stop = false;
      let cursor;
      const traces = [];
      while (!stop) {
        const { data } = await traceClient.query({
          query: queries.searchTracesQuery,
          variables: {
            workflowId: match.params.id,
            before: moment(endDate).add(1, 'day').format(),
            after: startDate,
            limit: LIMIT,
            cursor
          }
        });

        // Build the export as a list of trace states
        traces.push(
          ...data.workflow.traces.nodes.map(
            ({ rowId, createdAt, updatedAt, state }) => ({
              traceId: rowId,
              createdAt,
              updatedAt,
              data: state.data
            })
          )
        );

        // Update cursor or exit
        if (data.workflow.traces.pageInfo.hasNextPage) {
          cursor = data.workflow.traces.pageInfo.endCursor;
        } else {
          stop = true;
        }
      }
      if (traces.length) {
        downloadData({ workflow, traces, fileType });
      } else {
        notify.error('No traces have been found');
      }
    } catch (err) {
      console.error(err);
      notify.error('Something went wrong');
    }

    setIsExporting(false);
  }, [workflow, startDate, endDate, match.params.id, fileType]);

  const configHeader = {
    loading,
    bottomLevel: {
      infoContext: {
        links: [
          {
            label: 'Export data'
          }
        ]
      }
    }
  };

  return (
    <>
      <Navbar config={configHeader} />
      <div className={classes.content}>
        <h1 className={classes.title}>Export data</h1>
        <h1 className={classes.description}>
          Select a date range and export all the trace data for that period in
          CSV or Excel format.
        </h1>
        <div className={classes.form}>
          <div className={classes.field}>
            <DatePicker
              endDate={endDate}
              label="From"
              maxDate={today}
              onChange={onStartDateChange}
              selected={startDate}
              selectsStart
              startDate={startDate}
            />
          </div>
          <div className={classes.field}>
            <DatePicker
              endDate={endDate}
              label="To"
              maxDate={today}
              minDate={startDate}
              onChange={onEndDateChange}
              selected={endDate}
              selectsEnd
              startDate={startDate}
            />
          </div>
          <div className={classes.icon}>
            <Calendar />
          </div>
        </div>
        <div className={classes.radioButtons}>
          <RadioButton
            checked={fileType === 'csv'}
            dataCy="select"
            handleChange={() => setFileType('csv')}
            label="CSV (.csv)"
            showLabel
            value="csv"
          />
          <RadioButton
            checked={fileType === 'excel'}
            dataCy="select"
            handleChange={() => setFileType('excel')}
            label="Excel (.xlsx)"
            showLabel
            value="excel"
          />
        </div>
        {isExporting ? (
          <LoaderTraceLogo />
        ) : (
          <Pushbutton
            disabled={!(startDate && endDate)}
            onClick={onSubmit}
            primary
          >
            Download file
          </Pushbutton>
        )}
      </div>
      <Footer />
    </>
  );
};

WorkflowExport.propTypes = {
  classes: PropType.object.isRequired,
  match: PropType.object.isRequired,
  workflowQuery: PropType.object.isRequired
};

export default compose(
  graphql(FILTERS_QUERY, {
    name: 'filtersQuery'
  }),
  graphql(queries.workflowQuery, {
    name: 'workflowQuery',
    options: ({ match }) => ({
      variables: { workflowId: match.params.id }
    })
  }),
  injectSheet(styles)
)(WorkflowExport);
